import { AnimatedModal } from "@/src/components/AnimatedModal";
import { CloseIcon } from "@/src/components/CloseIcon";
import Loading from "@/src/components/Loading";
import { Tag } from "@/src/components/Tag";
import { Button as LegacyButton } from "@/src/components/tailwind/Button";
import { TextArea } from "@/src/components/tailwind/TextArea";
import { TextInput } from "@/src/components/tailwind/TextInput";
import { toasts } from "@/src/components/toasts/toasts";
import { Button } from "@/src/components/ui/button";
import {
  CreateDataRoomPermissionType,
  useCreateDataRoomPermissionMutation,
  useDeleteDataRoomPermissionMutation,
  useUpdateDataRoomPermissionMutation,
} from "@/src/graphql/generated";
import useGqlClient from "@/src/hooks/useGqlClient";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { PlusIcon } from "lucide-react";
import { useState } from "react";
import { useParams } from "react-router-dom";
import {
  SettingsCard,
  SettingsEmpty,
  SettingsRow,
} from "@/src/components/settings/SettingsLayout";

const PERMISSION_COLOR_CYCLE = [
  "persian",
  "green",
  "red",
  "blue",
  "yellow",
] as const;

function permissionTagColor(accessLevel: number) {
  return PERMISSION_COLOR_CYCLE[
    (accessLevel - 1) % PERMISSION_COLOR_CYCLE.length
  ];
}

const QUERY = `
  query DealPermissionStats($id: String!) {
    deal(id: $id) {
      dataRoom {
        id
        permissions {
          id
          name
          description
          accessLevel
          filesCount
          foldersCount
          dealGroupsCount
          dealAccountsCount
          dealInvitesCount
        }
      }
    }
  }
`;

type Permission = {
  id: string;
  name: string;
  description: string;
  accessLevel: number;
  filesCount: number;
  foldersCount: number;
  dealGroupsCount: number;
  dealAccountsCount: number;
  dealInvitesCount: number;
};

type QueryResult = {
  deal: {
    dataRoom: {
      id: string;
      permissions: Permission[];
    };
  };
};

function formatUsage(p: Permission): string {
  const parts = [
    `${p.foldersCount} folder${p.foldersCount === 1 ? "" : "s"}`,
    `${p.filesCount} file${p.filesCount === 1 ? "" : "s"}`,
    `${p.dealGroupsCount} group${p.dealGroupsCount === 1 ? "" : "s"}`,
    `${p.dealAccountsCount} account${p.dealAccountsCount === 1 ? "" : "s"}`,
    `${p.dealInvitesCount} invite${p.dealInvitesCount === 1 ? "" : "s"}`,
  ];
  return parts.join(" · ");
}

export function PermissionLevels() {
  const { dealId } = useParams<{ dealId: string }>();
  const client = useGqlClient();
  const [showCreate, setShowCreate] = useState(false);

  const query = useQuery<QueryResult>({
    queryKey: ["DealPermissionStats", { id: dealId }],
    queryFn: () => client.request<QueryResult>(QUERY, { id: dealId }),
    enabled: dealId !== "",
  });

  if (query.error) {
    return (
      <p className="text-sm text-muted-foreground">
        Failed to load permission levels
      </p>
    );
  }

  if (query.isLoading || !query.data) {
    return <Loading />;
  }

  const { permissions, id: dataRoomId } = query.data.deal.dataRoom;
  const sorted = [...permissions].sort((a, b) => a.accessLevel - b.accessLevel);

  return (
    <>
      <SettingsCard
        title="Permission levels"
        description="Access tiers used to gate folders and files in the Data Room."
        action={
          <Button
            type="button"
            size="sm"
            onClick={() => setShowCreate(true)}
          >
            <PlusIcon className="mr-1.5 h-3.5 w-3.5" />
            Create
          </Button>
        }
        contentClassName="divide-y divide-border p-0"
      >
        {sorted.length === 0 ? (
          <SettingsEmpty>
            No permission levels configured for this deal.
          </SettingsEmpty>
        ) : (
          sorted.map((p) => (
            <PermissionLevelRow key={p.id} permission={p} dealId={dealId} />
          ))
        )}
      </SettingsCard>

      <CreatePermissionModal
        open={showCreate}
        onClose={() => setShowCreate(false)}
        dataRoomId={dataRoomId}
        dealId={dealId}
        nextAccessLevel={permissions.length + 1}
      />
    </>
  );
}

function PermissionLevelRow(props: {
  permission: Permission;
  dealId: string;
}) {
  const { permission: p, dealId } = props;
  const [modal, setModal] = useState<"" | "edit" | "delete">("");

  const canDelete =
    p.foldersCount === 0 &&
    p.filesCount === 0 &&
    p.dealGroupsCount === 0 &&
    p.dealAccountsCount === 0 &&
    p.dealInvitesCount === 0;

  return (
    <>
      <SettingsRow>
        <div className="min-w-0 space-y-1">
          <Tag
            color={permissionTagColor(p.accessLevel)}
            text={p.name}
          />
          {p.description ? (
            <p className="text-sm text-muted-foreground">{p.description}</p>
          ) : null}
          <p className="text-sm text-muted-foreground/80">{formatUsage(p)}</p>
        </div>
        <div className="flex shrink-0 items-center gap-1">
          <Button
            type="button"
            variant="ghost"
            size="sm"
            onClick={() => setModal("edit")}
          >
            Edit
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="sm"
            disabled={!canDelete}
            title={
              canDelete
                ? "Delete"
                : "Cannot delete — permission level is in use"
            }
            onClick={() => canDelete && setModal("delete")}
            className="text-destructive hover:text-destructive"
          >
            Delete
          </Button>
        </div>
      </SettingsRow>

      <EditPermissionModal
        open={modal === "edit"}
        onClose={() => setModal("")}
        permission={p}
        dealId={dealId}
      />
      <DeletePermissionModal
        open={modal === "delete"}
        onClose={() => setModal("")}
        permission={p}
        dealId={dealId}
      />
    </>
  );
}

function CreatePermissionModal(props: {
  open: boolean;
  onClose: () => void;
  dataRoomId: string;
  dealId: string;
  nextAccessLevel: number;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const createPermission = useCreateDataRoomPermissionMutation(client);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");

  function handleClose() {
    setName("");
    setDescription("");
    props.onClose();
  }

  return (
    <AnimatedModal open={props.open} onClose={handleClose} size="lg">
      <div className="flex items-center justify-between mb-4">
        <p className="font-semibold text-foreground">Create permission level</p>
        <CloseIcon onClose={handleClose} />
      </div>

      <TextInput
        label="Name"
        placeholder="Phase 1"
        secondaryLabel=""
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />

      <div className="mt-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">Description</p>
        <p className="text-xs text-muted-foreground">Optional</p>
      </div>
      <TextArea
        label="Description"
        placeholder=""
        name="description"
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
      />

      <div className="mt-6 flex justify-end">
        <LegacyButton
          text="Create"
          variant="positive"
          isLoading={createPermission.isPending}
          isDisabled={!name.trim()}
          onClick={() => {
            if (createPermission.isPending || !name.trim()) return;
            createPermission.mutate(
              {
                dataRoomID: props.dataRoomId,
                name,
                description,
                accessLevel: props.nextAccessLevel,
                type: CreateDataRoomPermissionType.Normal,
              },
              {
                onSuccess: () => {
                  toasts.success("Permission level created");
                  queryClient.invalidateQueries({
                    queryKey: ["DealPermissionStats", { id: props.dealId }],
                  });
                  handleClose();
                },
                onError: () => {
                  toasts.error("Failed to create permission level");
                },
              },
            );
          }}
        />
      </div>
    </AnimatedModal>
  );
}

function EditPermissionModal(props: {
  open: boolean;
  onClose: () => void;
  permission: Permission;
  dealId: string;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updatePermission = useUpdateDataRoomPermissionMutation(client);
  const [name, setName] = useState(props.permission.name);
  const [description, setDescription] = useState(props.permission.description);

  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="lg">
      <div className="flex items-center justify-between mb-4">
        <p className="font-semibold text-foreground">Edit permission level</p>
        <CloseIcon onClose={props.onClose} />
      </div>

      <TextInput
        label="Name"
        placeholder="Phase 1"
        secondaryLabel=""
        value={name}
        onChange={(e) => setName(e.currentTarget.value)}
      />

      <div className="mt-3 flex items-center justify-between">
        <p className="text-sm font-semibold text-foreground">Description</p>
        <p className="text-xs text-muted-foreground">Optional</p>
      </div>
      <TextArea
        label="Description"
        placeholder=""
        name="description"
        rows={2}
        value={description}
        onChange={(e) => setDescription(e.currentTarget.value)}
      />

      <div className="mt-6 flex justify-end">
        <LegacyButton
          text="Save"
          variant="positive"
          isLoading={updatePermission.isPending}
          isDisabled={!name.trim()}
          onClick={() => {
            if (updatePermission.isPending || !name.trim()) return;
            updatePermission.mutate(
              { id: props.permission.id, name, description },
              {
                onSuccess: () => {
                  toasts.success("Permission level updated");
                  queryClient.invalidateQueries({
                    queryKey: ["DealPermissionStats", { id: props.dealId }],
                  });
                  props.onClose();
                },
                onError: () => {
                  toasts.error("Failed to update permission level");
                },
              },
            );
          }}
        />
      </div>
    </AnimatedModal>
  );
}

function DeletePermissionModal(props: {
  open: boolean;
  onClose: () => void;
  permission: Permission;
  dealId: string;
}) {
  const client = useGqlClient();
  const queryClient = useQueryClient();
  const deletePermission = useDeleteDataRoomPermissionMutation(client);

  return (
    <AnimatedModal open={props.open} onClose={props.onClose} size="lg">
      <div className="flex items-center justify-between mb-4">
        <p className="font-semibold text-foreground">Delete permission level</p>
        <CloseIcon onClose={props.onClose} />
      </div>

      <p className="text-sm text-muted-foreground">
        Are you sure you want to delete{" "}
        <span className="font-semibold text-foreground">
          {props.permission.name}
        </span>
        ? This action cannot be undone.
      </p>

      <div className="mt-6 flex justify-end gap-2">
        <LegacyButton text="Cancel" variant="neutral" onClick={props.onClose} />
        <LegacyButton
          text="Delete"
          variant="danger"
          isLoading={deletePermission.isPending}
          onClick={() => {
            if (deletePermission.isPending) return;
            deletePermission.mutate(
              { id: props.permission.id },
              {
                onSuccess: () => {
                  toasts.success("Permission level deleted");
                  queryClient.invalidateQueries({
                    queryKey: ["DealPermissionStats", { id: props.dealId }],
                  });
                  props.onClose();
                },
                onError: () => {
                  toasts.error("Failed to delete permission level");
                },
              },
            );
          }}
        />
      </div>
    </AnimatedModal>
  );
}
